import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ArrowLeft, UserPlus, Trash2, Users } from "lucide-react";
import { toast } from "sonner";

export default function AdminEditors() {
  const [editors, setEditors] = useState([]);
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/admins");
      setEditors(r.data);
    } catch { toast.error("Failed to load editors"); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setLoading(true);
    try {
      await api.post("/admins", { email, password });
      toast.success("Editor added");
      setEmail(""); setPassword(""); setOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add editor");
    } finally { setLoading(false); }
  };

  const remove = async (em) => {
    if (!window.confirm(`Remove editor ${em}?`)) return;
    try {
      await api.delete(`/admins/${encodeURIComponent(em)}`);
      toast.success("Editor removed");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="max-w-[900px] mx-auto px-6 py-10">
      <Link to="/admin" className="inline-flex items-center gap-2 font-mono-plex text-xs uppercase tracking-wider hover:underline" data-testid="back-to-admin">
        <ArrowLeft className="w-4 h-4" /> Back to admin
      </Link>
      <div className="flex items-end justify-between mt-4 border-b border-black/15 pb-6">
        <div>
          <div className="overline">/editor desk</div>
          <h1 className="font-display text-4xl font-black tracking-tighter mt-2">Editors</h1>
          <p className="text-sm text-[#555] mt-2 max-w-xl">
            Add additional editors to enable two-editor publishing — a separate person reviews and publishes another's drafts.
          </p>
        </div>
        <Button onClick={() => setOpen(true)} className="rounded-none bg-black text-white h-11" data-testid="add-editor-btn">
          <UserPlus className="w-4 h-4 mr-2" /> Add editor
        </Button>
      </div>

      <div className="mt-6 bg-white border border-black/15">
        <div className="bg-[#F9F9F9] border-b border-black/10 px-4 py-2 font-mono-plex text-[11px] uppercase tracking-[0.2em] text-[#555] flex items-center gap-2">
          <Users className="w-3.5 h-3.5" /> {editors.length} editor{editors.length === 1 ? "" : "s"} on file
        </div>
        <ul className="divide-y divide-black/10">
          {editors.map((e) => (
            <li key={e.email} className="px-4 py-3 flex items-center justify-between" data-testid={`editor-row-${e.email}`}>
              <div>
                <div className="font-medium">{e.email}</div>
                <div className="font-mono-plex text-[11px] uppercase tracking-[0.2em] text-[#555]">added {(e.created_at || "").slice(0, 10) || "—"}</div>
              </div>
              <Button variant="outline" onClick={() => remove(e.email)} className="rounded-none border-[#D32F2F] text-[#D32F2F] h-9" data-testid={`delete-editor-${e.email}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </li>
          ))}
        </ul>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-w-md border-2 border-black p-0 gap-0 bg-white">
          <DialogHeader className="px-6 py-4 border-b border-black/15">
            <DialogTitle className="font-display text-2xl font-bold tracking-tight">Add a new editor</DialogTitle>
            <DialogDescription className="text-xs font-mono-plex uppercase tracking-wider text-[#555]">
              They'll be able to draft, verify, and publish entries.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={create} className="p-6 space-y-3" data-testid="add-editor-form">
            <label className="block">
              <span className="overline mb-1.5 block">Email</span>
              <Input data-testid="editor-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="rounded-none border-black/30 h-11" />
            </label>
            <label className="block">
              <span className="overline mb-1.5 block">Initial password (≥8 chars)</span>
              <Input data-testid="editor-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} className="rounded-none border-black/30 h-11" />
              <span className="text-[11px] text-[#555] mt-1 block">Share securely; they should change it after first login.</span>
            </label>
            <DialogFooter className="pt-3 gap-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-none border-black/40 h-10">Cancel</Button>
              <Button type="submit" disabled={loading} className="rounded-none bg-black text-white h-10" data-testid="editor-create-btn">
                {loading ? "Adding…" : "Add editor"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
